import { ChangeEvent, FC, FormEvent, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { Password } from 'primereact/password';
import { useTranslation } from 'react-i18next';
import { validatePassword } from '@/utils/formValidations';
import Button from '../Button/Button';
import styles from './ChangePasswordForm.module.scss';

const initialPassworRequirements = {
  minLength: false,
  hasUppercase: false,
  hasLowercase: false,
  hasNumber: false,
  hasSpecialChar: false,
};

type ChangePasswordFormProps = {
  onSubmit: (newPassword: string, oldPassword?: string) => void;
  isLoading: boolean;
  submitButtonText: string;
  isChangingPassword?: boolean;
};

const ChangePasswordForm: FC<ChangePasswordFormProps> = ({
  onSubmit,
  isLoading,
  submitButtonText,
  isChangingPassword,
}) => {
  const { t } = useTranslation('changePassword');
  const [currentPassword, setCurrentPassword] = useState('');
  const [isSameAsCurrent, setIsSameAsCurrent] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  const [passwordsDontMatch, setPasswordsDontMatch] = useState(false);

  const [passwordRequirements, setPasswordRequirements] = useState(
    initialPassworRequirements
  );

  const allPasswordRequirementsMet = Object.values(passwordRequirements).every(
    (requirement) => requirement === true
  );

  const handleCurrentPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setCurrentPassword(password);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);

    if (isChangingPassword) {
      setIsSameAsCurrent(password === currentPassword);
    }

    const validations = validatePassword(password);
    setPasswordRequirements(validations);
  };

  const handleRepeatPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setRepeatPassword(password);
    setPasswordsDontMatch(newPassword !== password);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const passwordsMatch = newPassword === repeatPassword;
    setPasswordsDontMatch(!passwordsMatch);

    const isNewPasswordValid = isChangingPassword
      ? newPassword !== currentPassword && allPasswordRequirementsMet
      : allPasswordRequirementsMet;

    const canSubmit = passwordsMatch && isNewPasswordValid;

    if (canSubmit && isChangingPassword) {
      onSubmit(newPassword, currentPassword);
    }
    if (canSubmit && !isChangingPassword) {
      onSubmit(newPassword);
    }
  };

  return (
    <>
      <div className={styles.requirements}>
        <div>
          <FontAwesomeIcon icon={faCircleInfo} />
          <h4>{t('passwordRequirementInfo')}</h4>
        </div>
        <ul>
          <li>
            {t('passwordRequirementList.minLength')}
            {passwordRequirements.minLength && (
              <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
            )}
          </li>
          <li>
            {t('passwordRequirementList.uppercaseLetter')}
            {passwordRequirements.hasUppercase && (
              <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
            )}
          </li>
          <li>
            {t('passwordRequirementList.lowercaseLetter')}
            {passwordRequirements.hasLowercase && (
              <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
            )}
          </li>
          <li>
            {t('passwordRequirementList.number')}
            {passwordRequirements.hasNumber && (
              <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
            )}
          </li>
          <li>
            {t('passwordRequirementList.specialCharacter')}
            {passwordRequirements.hasSpecialChar && (
              <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
            )}
          </li>
        </ul>
      </div>
      <form className={styles.changePasswordForm} onSubmit={handleSubmit}>
        {isChangingPassword && (
          <div className={styles.field}>
            <label htmlFor="currentPassword">{t('currentPassword')}</label>
            <Password
              id="currentPassword"
              name="currentPassword"
              placeholder={t('currentPassword')}
              value={currentPassword}
              onChange={handleCurrentPasswordChange}
              toggleMask
              feedback={false}
              className={styles.input}
            />
          </div>
        )}
        <div className={styles.field}>
          <label htmlFor="newPassword">{t('newPasswordLabel')}</label>
          <Password
            id="newPassword"
            name="newPassword"
            placeholder={t('newPasswordPlaceholder')}
            value={newPassword}
            onChange={handlePasswordChange}
            toggleMask
            feedback={false}
            className={styles.input}
          />
          {isSameAsCurrent && isChangingPassword && (
            <p className={styles.errorMessage}>
              {t('newPasswordCannotBeSame')}
            </p>
          )}
        </div>
        <div className={styles.field}>
          <label htmlFor="repeatPassword">{t('repeatPasswordLabel')}</label>
          <Password
            id="repeatPassword"
            name="repeatPassword"
            placeholder={t('repeatPasswordPlaceholder')}
            value={repeatPassword}
            onChange={handleRepeatPasswordChange}
            toggleMask
            feedback={false}
            className={styles.input}
          />
          {passwordsDontMatch && (
            <p className={styles.errorMessage}>{t('passwordsDontMatch')}</p>
          )}
        </div>
        <Button
          disabled={
            isLoading ||
            !allPasswordRequirementsMet ||
            newPassword !== repeatPassword
          }
          type="submit"
          className={styles.button}
        >
          {submitButtonText}
        </Button>
      </form>
    </>
  );
};

export default ChangePasswordForm;
